// M2 lawsuits & licensing (M2_SPEC §14, DESIGN §13.3/13.7).
// Historical patent holders (Hughes, Halliburton, Cameron, Seismos...) charge a fee per use of a covered item unless the
// player invented it first, holds the patent, or bought a licence. Knock-off copies are 30% cheaper and invite
// lawsuits. Players' own patents get infringed; they can sue, license the infringer, or let it go.
import { chance, nextFloat } from '../../core/rng.js';
import { dayToDate } from '../../core/time.js';
import { HISTORICAL_PATENTS, PATENTED_ITEMS, historicalPatentActive } from '../../data/m2/tech.js';
import { TECH, licenseCost, industryAdoption, patentRoyaltyPerYear } from '../../data/tech.js';
import { unitPrice, eraDollars, yf } from '../defs.js';
import { queueDecision, popDecision } from '../security.js';
import { clamp, notify, spend, earn, nextId, fmt$ } from '../util.js';

export const BOOTLEG = { priceMult: 0.7, suitPerMonth: 0.03, fineFeeMult: 10, rep: -3 };
export const DEFENSE = { legalCost: 2000, baseYear: 1910, days: 90, baseWin: 0.25, repWin: 0.25, loseFineMult: 1.5, loseRep: -5 };
export const SUIT = { cost: 2000, baseYear: 1910, days: 90, baseWin: 0.4, royaltyYears: 3, licenseRoyaltyMult: 1.2, infringePerMonth: 0.05 };
// Mirrors sim/tick.js PATENT_INCOME_MULT (not imported: tick.js imports this module).
export const PATENT_INCOME_MULT = 0.02;
// Units charged when the item is bought (per unit bought). 'well' (BOP) is charged per well drilled with it.
const BUY_UNITS = new Set(['bit', 'job', 'tank', 'crew', 'survey']);

export function licensingState(state) {
  state.licensing ??= { bootleg: {}, suits: {}, feesPaid: {}, infringements: {} };
  state.licensing.infringements ??= {};
  return state.licensing;
}

export function hasLicense(state, techId) { return !!state.research?.licenses?.includes(techId); }
// Exempt: own invention (any mode; prior art), own patent, or a licence.
export function exemptFrom(state, techId) {
  return hasLicense(state, techId) || !!state.research?.done?.[techId];
}

// Fee record for using `qty` units of equipment `defId` now, or null if nothing is owed.
export function patentFeeFor(state, defId, qty = 1) {
  const techId = PATENTED_ITEMS[defId];
  if (!techId) return null;
  const p = HISTORICAL_PATENTS[techId];
  if (!p || p.serviceOnly || !(p.feePerUse > 0)) return null;
  if (!historicalPatentActive(techId, yf(state)) || exemptFrom(state, techId)) return null;
  const per = eraDollars(state, p.feePerUse, p.baseYear);
  return { techId, defId, holder: p.holder, unit: p.unit, perUnit: per, qty, fee: per * qty };
}

export function chargePatentFee(state, rec) {
  if (!rec || !(rec.fee > 0)) return 0;
  spend(state, rec.fee, 'licenses', false);
  const L = licensingState(state);
  L.feesPaid[rec.techId] = (L.feesPaid[rec.techId] ?? 0) + rec.fee;
  return rec.fee;
}

// ---- shop hooks (core/actions.buyEquipment wrapper) --------------------------------------------
// Returns { ok:false,msg } to refuse, or a plan { fee, bootleg, refund } (possibly null when nothing applies).
export function preBuy(state, def, qty, opts = {}) {
  const techId = PATENTED_ITEMS[def.id];
  const p = techId ? HISTORICAL_PATENTS[techId] : null;
  const fee = p && BUY_UNITS.has(p.unit) ? patentFeeFor(state, def.id, unitsFor(state, def, qty, p.unit)) : null;
  if (opts.bootleg) {
    if (!techId || !historicalPatentActive(techId, yf(state)) || exemptFrom(state, techId) || p?.serviceOnly) return { ok: false, msg: `${def.name} is not under patent: there is no knock-off to buy.` };
    if (['safety', 'storage', 'survey'].includes(def.cat)) return { ok: false, msg: `Nobody sells knock-off ${def.name}s.` };
    return { plan: { bootleg: true, techId, refund: unitPrice(state, def) * qty * (1 - BOOTLEG.priceMult), qty, fee: 0 } };
  }
  if (!fee) return { plan: null };
  const total = unitPrice(state, def) * qty + fee.fee;
  if (state.company.cash < total - 1e-9) return { ok: false, msg: `${def.name} costs ${fmt$(total)} with the ${fmt$(fee.fee)} patent fee to ${fee.holder}; not enough cash.` };
  return { plan: { fee } };
}

function unitsFor(state, def, qty, unit) {
  if (unit === 'crew') return Math.max(1, Object.values(state.crew).filter((c) => c.status === 'active').length);
  return qty;
}

export function postBuy(state, def, plan) {
  if (!plan) return '';
  if (plan.bootleg) {
    state.company.cash += plan.refund;
    const cat = ['rig', 'engine', 'building', 'storage', 'safety'].includes(def.cat) ? 'capex' : 'consumables';
    const m = state.finance.current;
    m[cat] = (m[cat] ?? 0) - plan.refund;
    const L = licensingState(state);
    const b = (L.bootleg[plan.techId] ??= { uses: 0, since: state.day });
    b.uses += plan.qty;
    return ` Knock-off: saved ${fmt$(plan.refund)}. ${HISTORICAL_PATENTS[plan.techId].holder}'s lawyers may come calling.`;
  }
  if (plan.fee) { chargePatentFee(state, plan.fee); return ` Patent fee ${fmt$(plan.fee.fee)} to ${plan.fee.holder}.`; }
  return '';
}

// ---- licences ----------------------------------------------------------------------------
export function licenseQuote(state, techId) {
  const t = TECH[techId];
  const p = HISTORICAL_PATENTS[techId];
  if (!t || !p) return null;
  const y = yf(state);
  return { techId, cost: licenseCost(t, Math.max(y, t.baseYear)), holder: p.holder, active: historicalPatentActive(techId, y), licensed: hasLicense(state, techId), exempt: exemptFrom(state, techId) };
}

export function buyLicenseImpl(state, techId) {
  const q = licenseQuote(state, techId);
  if (!q) return { ok: false, msg: 'Nobody holds a patent on that.' };
  if (q.exempt) return { ok: false, msg: hasLicense(state, techId) ? 'You already hold a licence.' : 'Your own invention: no licence needed.' };
  if (!q.active && yf(state) >= HISTORICAL_PATENTS[techId].until) return { ok: false, msg: 'That patent has expired; use it freely.' };
  if (!spend(state, q.cost, 'licenses')) return { ok: false, msg: `A licence from ${q.holder} costs ${fmt$(q.cost)}.` };
  (state.research.licenses ??= []).push(techId);
  const L = licensingState(state);
  if (L.bootleg[techId]) L.bootleg[techId].uses = 0; // licensing settles past copying too
  return { ok: true, msg: `Licensed the ${TECH[techId].name} from ${q.holder} for ${fmt$(q.cost)}. No more fees.` };
}

// ---- monthly: bootleg suits against us, infringement of our patents ------------------------------------
export function tickLicensingMonthly(state) {
  const L = state.licensing;
  const y = yf(state);
  if (L) {
    for (const [techId, b] of Object.entries(L.bootleg)) {
      if (!(b.uses > 0)) continue;
      if (Object.values(L.suits).some((s) => s.techId === techId && s.kind === 'againstUs' && s.status !== 'closed')) continue;
      if (!chance(state, BOOTLEG.suitPerMonth)) continue;
      startSuitAgainstUs(state, techId, b.uses);
    }
  }
  for (const [techId, d] of Object.entries(state.research?.done ?? {})) {
    if (d.mode !== 'patent' || (d.patentExpires && state.day > d.patentExpires)) continue;
    const t = TECH[techId];
    if (!t || !(t.royaltyPerUse > 0)) continue;
    const adoption = industryAdoption(t, y, d.inventedYear);
    const LS = licensingState(state);
    if (Object.values(LS.infringements).some((i) => i.techId === techId && i.status === 'pending')) continue;
    if (Object.values(LS.suits).some((s) => s.techId === techId && s.kind === 'infringer' && s.status !== 'closed')) continue;
    if (!chance(state, SUIT.infringePerMonth * adoption)) continue;
    startInfringement(state, techId, d);
  }
}

function annualRoyalty(state, techId) {
  const d = state.research.done[techId];
  return patentRoyaltyPerYear(TECH[techId], yf(state), d.inventedYear) * PATENT_INCOME_MULT * (d.royaltyMult ?? 1);
}

function startInfringement(state, techId, d) {
  const L = licensingState(state);
  const id = nextId(state, 'pi');
  const cost = eraDollars(state, SUIT.cost, SUIT.baseYear);
  const win = clamp(SUIT.baseWin + state.company.reputation / 200, 0, 0.95);
  const award = annualRoyalty(state, techId) * SUIT.royaltyYears;
  L.infringements[id] = { id, techId, day: state.day, status: 'pending', cost, win, award };
  const t = TECH[techId];
  queueDecision(state, {
    kind: 'infringement', id, infringementId: id, techId, day: state.day, title: `Patent infringed: ${t.name}`,
    text: `A rival outfit is selling copies of your ${t.name} without paying a cent.`,
    options: [
      { choice: 'sue', label: `Sue (${fmt$(cost)}, ${SUIT.days} days)`, desc: `Win ${Math.round(win * 100)}%: about ${fmt$(award)} in back royalties.`, cost, odds: win, disabled: state.company.cash < cost },
      { choice: 'license', label: 'Offer them a licence', desc: `Royalties on this patent +${Math.round((SUIT.licenseRoyaltyMult - 1) * 100)}%.` },
      { choice: 'ignore', label: 'Ignore it' },
    ],
  });
  notify(state, 'lawsuit', { techId, kind: 'infringement', text: `Your ${t.name} patent is being infringed.`, level: 'warn' });
}

export function resolveInfringementImpl(state, id, choice) {
  const L = licensingState(state);
  const inf = L.infringements[id];
  if (!inf || inf.status !== 'pending') return { ok: false, msg: 'Nothing pending.' };
  if (!['sue', 'license', 'ignore'].includes(choice)) return { ok: false, msg: 'Choose sue, license or ignore.' };
  popDecision(state, id);
  inf.status = choice;
  if (choice === 'sue') return fileSuitImpl(state, inf.techId, inf);
  if (choice === 'license') {
    const d = state.research.done[inf.techId];
    if (d) d.royaltyMult = (d.royaltyMult ?? 1) * SUIT.licenseRoyaltyMult;
    return { ok: true, msg: `They sign a licence. ${TECH[inf.techId].name} royalties +${Math.round((SUIT.licenseRoyaltyMult - 1) * 100)}%.` };
  }
  return { ok: true, msg: 'You let it go.' };
}

// Sue infringers of a patent you hold (also reachable without a pending infringement decision).
export function fileSuitImpl(state, techId, inf = null) {
  const d = state.research?.done?.[techId];
  if (!d || d.mode !== 'patent' || (d.patentExpires && state.day > d.patentExpires)) return { ok: false, msg: 'You hold no patent in force on that.' };
  const L = licensingState(state);
  if (Object.values(L.suits).some((s) => s.techId === techId && s.kind === 'infringer' && s.status !== 'closed')) return { ok: false, msg: 'A suit is already before the court.' };
  const cost = inf?.cost ?? eraDollars(state, SUIT.cost, SUIT.baseYear);
  if (!spend(state, cost, 'licenses')) return { ok: false, msg: `Lawyers want ${fmt$(cost)} up front.` };
  const id = nextId(state, 'ls');
  const win = inf?.win ?? clamp(SUIT.baseWin + state.company.reputation / 200, 0, 0.95);
  L.suits[id] = { id, techId, kind: 'infringer', day: state.day, resolveDay: state.day + SUIT.days, cost, winPct: win, award: inf?.award ?? annualRoyalty(state, techId) * SUIT.royaltyYears, status: 'open' };
  return { ok: true, msg: `Suit filed over the ${TECH[techId].name}. The court sits in ${SUIT.days} days.` };
}

function startSuitAgainstUs(state, techId, uses) {
  const L = licensingState(state);
  const p = HISTORICAL_PATENTS[techId];
  const id = nextId(state, 'ls');
  const fine = eraDollars(state, p.feePerUse, p.baseYear) * BOOTLEG.fineFeeMult * uses;
  const legal = eraDollars(state, DEFENSE.legalCost, DEFENSE.baseYear);
  const win = clamp(DEFENSE.baseWin + DEFENSE.repWin * state.company.reputation / 100, 0, 0.9);
  L.suits[id] = { id, techId, kind: 'againstUs', day: state.day, uses, fine, legal, winPct: win, status: 'pending' };
  queueDecision(state, {
    kind: 'patentSuit', id, suitId: id, techId, day: state.day, title: `${p.holder} sues you`,
    text: `${p.holder} has traced ${uses} knock-off ${TECH[techId]?.name ?? techId} unit${uses > 1 ? 's' : ''} to your company and filed suit.`,
    options: [
      { choice: 'settle', label: `Settle (${fmt$(fine)})`, desc: `Pay ${BOOTLEG.fineFeeMult}× the fee per copy. Reputation ${BOOTLEG.rep}.`, cost: fine },
      { choice: 'fight', label: `Fight it (${fmt$(legal)} legal)`, desc: `Win ${Math.round(win * 100)}% in ${DEFENSE.days} days; lose and pay ${fmt$(fine * DEFENSE.loseFineMult)}, reputation ${DEFENSE.loseRep}.`, cost: legal, odds: win },
    ],
  });
  notify(state, 'lawsuit', { techId, kind: 'againstUs', fine, pause: true, level: 'warn', text: `${p.holder} sues over your knock-off ${TECH[techId]?.name ?? techId}s.` });
}

export function resolvePatentSuitImpl(state, id, choice) {
  const L = licensingState(state);
  const s = L.suits[id];
  if (!s || s.status !== 'pending') return { ok: false, msg: 'No such suit pending.' };
  if (!['settle', 'fight'].includes(choice)) return { ok: false, msg: 'Choose settle or fight.' };
  popDecision(state, id);
  if (choice === 'settle') return settleSuitImpl(state, id);
  spend(state, s.legal, 'licenses', false);
  s.status = 'open'; s.resolveDay = state.day + DEFENSE.days;
  return { ok: true, msg: `Your lawyers go to court over the ${TECH[s.techId]?.name}.` };
}

export function settleSuitImpl(state, id) {
  const L = licensingState(state);
  const s = L.suits[id];
  if (!s || s.kind !== 'againstUs' || s.status === 'closed') return { ok: false, msg: 'Nothing to settle.' };
  if (s.status === 'pending') popDecision(state, id);
  spend(state, s.fine, 'fines', false);
  state.company.reputation = clamp(state.company.reputation + BOOTLEG.rep, 0, 100);
  if (L.bootleg[s.techId]) L.bootleg[s.techId].uses = 0;
  s.status = 'closed'; s.outcome = 'settled'; s.closedDay = state.day;
  notify(state, 'lawsuit', { techId: s.techId, kind: 'againstUs', won: false, amount: s.fine, text: `Settled with ${HISTORICAL_PATENTS[s.techId]?.holder}: ${fmt$(s.fine)}.` });
  return { ok: true, msg: `Settled for ${fmt$(s.fine)}.` };
}

// ---- daily: suits come to judgement; the Cameron BOP fee per well --------------------------------------
export function tickLicensing(state) {
  const L = state.licensing;
  if (L) {
    for (const s of Object.values(L.suits)) {
      if (s.status === 'pending' && state.day - s.day >= 7) resolvePatentSuitImpl(state, s.id, 'settle');
      if (s.status !== 'open' || state.day < s.resolveDay) continue;
      const won = nextFloat(state) < s.winPct;
      s.status = 'closed'; s.closedDay = state.day; s.outcome = won ? 'won' : 'lost';
      const st = state.company.stats;
      if (s.kind === 'infringer') {
        if (won) { earn(state, s.award, 'recoveries'); st.lawsuitsWon = (st.lawsuitsWon ?? 0) + 1; }
        else st.lawsuitsLost = (st.lawsuitsLost ?? 0) + 1;
        notify(state, 'lawsuit', { techId: s.techId, kind: 'infringer', won, amount: won ? s.award : 0, level: won ? 'major' : 'warn',
          text: won ? `Court finds for you over the ${TECH[s.techId]?.name}: ${fmt$(s.award)} in back royalties.` : `You lost the ${TECH[s.techId]?.name} patent suit.` });
      } else {
        if (won) { st.lawsuitsWon = (st.lawsuitsWon ?? 0) + 1; if (L.bootleg[s.techId]) L.bootleg[s.techId].uses = 0; }
        else {
          spend(state, s.fine * DEFENSE.loseFineMult, 'fines', false);
          state.company.reputation = clamp(state.company.reputation + DEFENSE.loseRep, 0, 100);
          if (L.bootleg[s.techId]) L.bootleg[s.techId].uses = 0;
          st.lawsuitsLost = (st.lawsuitsLost ?? 0) + 1;
        }
        notify(state, 'lawsuit', { techId: s.techId, kind: 'againstUs', won, amount: won ? 0 : s.fine * DEFENSE.loseFineMult, level: 'warn',
          text: won ? `The court threw out ${HISTORICAL_PATENTS[s.techId]?.holder}'s suit.` : `You lost to ${HISTORICAL_PATENTS[s.techId]?.holder}: ${fmt$(s.fine * DEFENSE.loseFineMult)}.` });
      }
    }
    for (const [id, s] of Object.entries(L.suits)) if (s.status === 'closed' && state.day - s.closedDay > 730) delete L.suits[id];
    for (const [id, i] of Object.entries(L.infringements)) if (i.status !== 'pending' && state.day - i.day > 730) delete L.infringements[id];
  }
  // ram BOP: Cameron's fee is per well drilled with the preventer
  for (const w of Object.values(state.wells)) {
    if (w.bopFeePaid || !w.rigId) continue;
    const rig = state.rigs[w.rigId];
    if (!rig?.bopUid) continue;
    const item = state.items[rig.bopUid];
    if (!item) continue;
    w.bopFeePaid = true;
    if (item.bootleg) { const b = (licensingState(state).bootleg[PATENTED_ITEMS[item.defId] ?? 'ram_bop'] ??= { uses: 0, since: state.day }); b.uses++; continue; }
    const rec = patentFeeFor(state, item.defId, 1);
    if (rec) chargePatentFee(state, rec);
  }
}

// ---- view ---------------------------------------------------------------------------------
export function licensingView(state) {
  const y = yf(state);
  const L = state.licensing ?? { bootleg: {}, suits: {}, feesPaid: {}, infringements: {} };
  const patents = Object.entries(HISTORICAL_PATENTS).map(([techId, p]) => {
    const items = Object.entries(PATENTED_ITEMS).filter(([, t]) => t === techId).map(([d]) => d);
    const q = licenseQuote(state, techId);
    return {
      techId, name: TECH[techId]?.name ?? techId, holder: p.holder, unit: p.unit, from: p.from, until: p.until, serviceOnly: !!p.serviceOnly,
      fee: eraDollars(state, p.feePerUse, p.baseYear), active: historicalPatentActive(techId, y), licensed: hasLicense(state, techId),
      exempt: exemptFrom(state, techId), ownInvention: !!state.research?.done?.[techId], licenseCost: q?.cost ?? null,
      items, bootlegUses: L.bootleg[techId]?.uses ?? 0, feesPaid: L.feesPaid[techId] ?? 0,
      canBootleg: historicalPatentActive(techId, y) && !exemptFrom(state, techId) && !p.serviceOnly && items.length > 0,
    };
  });
  const myPatents = Object.entries(state.research?.done ?? {}).filter(([, d]) => d.mode === 'patent').map(([techId, d]) => ({
    techId, name: TECH[techId]?.name ?? techId, expires: d.patentExpires, royaltyMult: d.royaltyMult ?? 1, active: !d.patentExpires || state.day <= d.patentExpires,
    annualRoyalty: state.research.done[techId] ? annualRoyalty(state, techId) : 0,
    infringements: Object.values(L.infringements).filter((i) => i.techId === techId).length,
  }));
  return { patents, myPatents, suits: Object.values(L.suits), infringements: Object.values(L.infringements), bootlegPriceMult: BOOTLEG.priceMult, month: dayToDate(state.day).month };
}

export const isBuyUnit = (unit) => BUY_UNITS.has(unit);
